import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { adminApi, type ClientAnalyticsDto } from '@/api/admin'
import { EmptyChart, SectionTitle, SortableTh } from './AnalyticsShared'
import { useSortable, useAnalyticsMoney } from './analyticsHelpers'

export function ClientsTab({ from, to }: { from: string | null; to: string | null }) {
  const { fmtMoney, fmtMoneyShort } = useAnalyticsMoney()
  const { data = [], isLoading } = useQuery({
    queryKey: ['analytics-clients', from, to],
    queryFn: () => adminApi.getClientAnalytics(from, to).then(r => r.data),
  })

  type ClientRow = ClientAnalyticsDto & { total: number }
  const rows: ClientRow[] = data.map(c => ({ ...c, total: c.totalRevenue + c.outstandingAmount }))
  const { sorted, toggle, sortKey, sortDir } = useSortable<ClientRow>(rows, 'totalRevenue')

  const chartData = [...data]
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 10)
    .map(c => ({
      name: c.clientName.length > 18 ? c.clientName.slice(0, 18) + '…' : c.clientName,
      fullName: c.clientName,
      revenue: c.totalRevenue,
      outstanding: c.outstandingAmount,
    }))

  if (isLoading) return <p className="text-gray-400 text-sm py-8">Loading…</p>

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <SectionTitle>Top clients by revenue (top 10)</SectionTitle>
        <div className="mt-4 h-64">
          {chartData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, n) => [fmtMoney(Number(v)), n]}
                  labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''}
                />
                <Legend />
                <Bar dataKey="revenue"     name="Revenue"     fill="#6366f1" radius={[0, 4, 4, 0]} stackId="a" />
                <Bar dataKey="outstanding" name="Outstanding" fill="#fbbf24" radius={[0, 4, 4, 0]} stackId="b" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <SortableTh col="clientName"        sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Client</SortableTh>
              <SortableTh col="totalBookings"     sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Bookings</SortableTh>
              <SortableTh col="completedBookings" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Completed</SortableTh>
              <SortableTh col="totalRevenue"      sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Revenue (paid)</SortableTh>
              <SortableTh col="outstandingAmount" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Outstanding</SortableTh>
              <SortableTh col="total"            sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Total</SortableTh>
              <SortableTh col="certificatesIssued" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Certs issued</SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">No client data for this period</td></tr>
            ) : sorted.map(c => (
              <tr key={c.clientId} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-gray-900">{c.clientName}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.totalBookings}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-700 font-medium">{c.completedBookings}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">{fmtMoney(c.totalRevenue)}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={c.outstandingAmount > 0 ? 'text-amber-600 font-medium' : 'text-gray-300'}>
                    {c.outstandingAmount > 0 ? fmtMoney(c.outstandingAmount) : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">{fmtMoney(c.total)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.certificatesIssued}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
